import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { type ComponentProps, memo } from 'react';

import { cn } from '@air/utils-tailwind';

export type RadioGroupItemAlign = 'start' | 'center' | 'end';
export type RadioGroupItemSide = 'top' | 'right' | 'bottom' | 'left';

const RADIO_GROUP_ITEM_ALIGN_CLASS_NAME = {
  center: 'items-center',
  end: 'items-end',
  start: 'items-start',
};

const RADIO_GROUP_ITEM_INDICATOR_ALIGN_CLASS_NAME = {
  center: 'my-0',
  end: 'mb-1',
  start: 'mt-1',
};

const RADIO_GROUP_ITEM_SIDE_CLASS_NAME: { [key in RadioGroupItemSide]: string } = {
  bottom: 'flex-col-reverse',
  left: 'flex-row',
  right: 'flex-row-reverse',
  top: 'flex-col',
};

export type RadioGroupItemProps = Omit<ComponentProps<'label'>, 'id'> &
  Pick<RadixRadioGroup.RadioGroupItemProps, 'disabled' | 'required' | 'value'> &
  Required<Pick<RadixRadioGroup.RadioGroupItemProps, 'id'>> & {
    align?: RadioGroupItemAlign;
    indicatorClassName?: string;
    side?: RadioGroupItemSide;
    sideOffset?: number;
  };

export const RadioGroupItem = memo(
  ({
    align = 'center',
    children,
    className,
    disabled,
    id,
    indicatorClassName,
    required,
    side = 'left',
    sideOffset = 8,
    style,
    value,
    ...restOfProps
  }: RadioGroupItemProps) => {
    return (
      <label
        className={cn(
          'flex font-medium ads:text-primary text-14 text-gray-12',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          RADIO_GROUP_ITEM_ALIGN_CLASS_NAME[align],
          RADIO_GROUP_ITEM_SIDE_CLASS_NAME[side],
          className,
        )}
        htmlFor={id}
        style={{
          gap: sideOffset,
          ...style,
        }}
        {...restOfProps}
      >
        <RadixRadioGroup.Item
          className={cn(
            // biome-ignore lint/plugin: the disabled border already uses ! to beat the checked border
            'group/radio-group-item disabled:!border-gray-5 h-4 w-4 shrink-0 rounded-full border border-gray-9 radix-disabled:border-gray-5 radix-state-checked:ads:border-primary radix-state-checked:border-blue-9 p-0.5 hover:radix-state-checked:ads:border-primary hover:radix-state-checked:border-blue-10 active:radix-state-checked:ads:border-primary active:radix-state-checked:border-blue-10 disabled:cursor-not-allowed',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            RADIO_GROUP_ITEM_INDICATOR_ALIGN_CLASS_NAME[align],
            indicatorClassName,
          )}
          disabled={disabled}
          id={id}
          required={required}
          value={value}
        >
          <RadixRadioGroup.Indicator
            // biome-ignore lint/plugin: the disabled fill already uses ! so the ADS twin has to as well, or the legacy color wins with the flag on
            className="ads:group-disabled/radio-group-item:!bg-primary-subtle group-disabled/radio-group-item:!bg-blue-3 block size-full rounded-full ads:bg-primary bg-blue-9 ads:group-hover/radio-group-item:bg-hover-primary group-hover/radio-group-item:bg-blue-10 ads:group-active/radio-group-item:bg-hover-primary group-active/radio-group-item:bg-blue-10"
          />
        </RadixRadioGroup.Item>
        {children}
      </label>
    );
  },
);

RadioGroupItem.displayName = 'RadioGroupItem';
